import React, { PropsWithChildren } from "react";
import useEmblaCarousel, { EmblaCarouselType } from "embla-carousel-react";
import { useTheme } from "styled-components";

import { CaretLeft, CaretRight } from "@ui/Assets/MyIcons";
import { TestProps, TestWrapper } from "@ui/Atoms";

import * as Styled from "./Carousel.styled";

export const Carousel: React.FC<
  PropsWithChildren<
    TestProps & {
      slidesPerViewTablet?: number;
      slidesPerViewDesktop?: number;
    }
  >
> = ({ children, testId, ...props }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
  });
  const theme = useTheme();

  const [isNextStep, setIsNextStep] = React.useState(false);
  const [isPrevStep, setIsPrevStep] = React.useState(false);

  React.useEffect(() => {
    if (!emblaApi) return;

    const updateHandler = ({
      canScrollPrev,
      canScrollNext,
    }: EmblaCarouselType) => {
      setIsNextStep(canScrollNext());
      setIsPrevStep(canScrollPrev());
    };

    updateHandler(emblaApi);

    emblaApi.on("select", updateHandler);

    return () => {
      emblaApi.off("select", updateHandler);
    };
  }, [emblaApi]);

  return (
    <TestWrapper testId={testId}>
      <Styled.Root>
        <Styled.ArrowButton
          $version="prev"
          $isActive={isPrevStep}
          onClick={() => emblaApi?.scrollPrev()}
        >
          <CaretLeft width={theme.icons.sizes.base_x4} />
        </Styled.ArrowButton>
        <Styled.Viewport
          ref={emblaRef}
          $slidesPerViewDesktop={props.slidesPerViewDesktop}
          $slidesPerViewTablet={props.slidesPerViewTablet}
        >
          <Styled.Container>
            {React.Children.map(children, child => {
              if (!React.isValidElement(child)) return null;
              return <Styled.Slide>{child}</Styled.Slide>;
            })}
          </Styled.Container>
        </Styled.Viewport>
        <Styled.ArrowButton
          $version="next"
          $isActive={isNextStep}
          onClick={() => emblaApi?.scrollNext()}
        >
          <CaretRight width={theme.icons.sizes.base_x4} />
        </Styled.ArrowButton>
      </Styled.Root>
    </TestWrapper>
  );
};
